import { useMemo, useState } from "react";
import { CalendarClock, Check, Infinity as InfinityIcon } from "lucide-react";

import { ComunicadoSistemaCriteria, ComunicadoSistemaFiltrosOptions } from "@workflow/types";

import { Column, DataTable } from "@/components/table/DataTable";
import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import { TopBarSection } from "@/components/shared/SecaoBarraSuperior";
import { StatusBadge } from "@/components/ui/badge-status";
import { cn } from "@/lib/utils";

import {
  NIVEL_CONFIG,
  ESTADO_LABEL,
  ESTADO_COLOR,
} from "@/components/comunicados/comunicado-nivel";
import { ComunicadoSistemaDialog } from "@/components/comunicados/ComunicadoSistemaDialog";
import {
  comunicadoSistemaKeys,
  comunicadoSistemaQueries,
} from "@/services/api/queries/comunicado-sistema.queries";
import { ComunicadoSistemaHistoricoDTO } from "@/types/comunicado-sistema.type";

const fmtData = (d: string) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "2-digit" });

/**
 * Histórico de comunicados do usuário: tudo que já esteve no ar para ele,
 * inclusive expirados e arquivados. Mesma listagem da tela admin, mas clicar
 * abre o dialog de leitura (§4.4) em vez de navegar para uma página.
 */
export default function ComunicadosHistoricoPage() {
  const [aberto, setAberto] = useState<ComunicadoSistemaHistoricoDTO | null>(null);

  const { tableProps, isError, invalidate } = useServerDataTable<
    ComunicadoSistemaHistoricoDTO,
    ComunicadoSistemaFiltrosOptions,
    ComunicadoSistemaCriteria
  >({
    queryOptions: comunicadoSistemaQueries.historico,
    filters: comunicadoSistemaQueries.historicoFiltros,
    invalidateKey: comunicadoSistemaKeys.historicos(),
  });

  const columns = useMemo<Column<ComunicadoSistemaHistoricoDTO>[]>(
    () => [
      {
        key: "comunicado.titulo",
        title: "Comunicado",
        sortable: true,
        raw: (c) => c.titulo,
        render: (c) => {
          const config = NIVEL_CONFIG[c.nivel];
          return (
            <div className="flex items-start gap-2.5 py-1">
              <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", config.dot)} />
              <div className="min-w-0">
                <p className="truncate font-medium text-foreground">{c.titulo}</p>
                <p className="truncate text-xs text-muted-foreground">{c.resumo}</p>
              </div>
            </div>
          );
        },
      },
      {
        key: "nivel",
        title: "Nível",
        width: "130px",
        render: (c) => {
          const config = NIVEL_CONFIG[c.nivel];
          return (
            <StatusBadge color={config.soft} className="gap-1.5 font-medium">
              <span className={cn("h-1.5 w-1.5 rounded-full", config.dot)} />
              {config.label}
            </StatusBadge>
          );
        },
      },
      {
        key: "estado",
        title: "Estado",
        width: "120px",
        render: (c) => (
          <StatusBadge color={ESTADO_COLOR[c.estado]} className="font-medium">
            {ESTADO_LABEL[c.estado]}
          </StatusBadge>
        ),
      },
      {
        key: "janela",
        title: "Vigência",
        width: "200px",
        render: (c) => (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5 shrink-0 opacity-60" />
            {fmtData(c.inicioEm)}
            <span className="opacity-40">→</span>
            {c.fimEm ? (
              fmtData(c.fimEm)
            ) : (
              <InfinityIcon className="h-3.5 w-3.5 shrink-0 opacity-60" />
            )}
          </span>
        ),
      },
      {
        key: "leitura",
        title: "Leitura",
        width: "130px",
        // Só o crítico exige aceite; nos demais a coluna fica vazia de propósito.
        render: (c) =>
          !c.exigeAceite ? (
            <span className="text-xs text-muted-foreground/60">—</span>
          ) : c.jaAceito ? (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-emerald-600 dark:text-emerald-400">
              <Check className="h-3.5 w-3.5 shrink-0" />
              Confirmada
            </span>
          ) : (
            <span className="whitespace-nowrap text-xs font-medium text-red-600 dark:text-red-400">
              Pendente
            </span>
          ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col space-y-6 pb-10 duration-500 animate-in fade-in">
      <TopBarSection
        entity="Comunicado"
        title="Comunicados"
        subtitle="Todos os avisos que já foram publicados para você. Clique para ler o conteúdo."
      />

      <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
        <DataTable
          {...tableProps}
          storageKey="comunicados-historico-v1"
          columns={columns}
          isError={isError}
          onRetry={invalidate}
          onRowClick={setAberto}
          defaultSortColumn="comunicado.inicioEm"
          defaultSortOrder="DESC"
          searchPlaceholder="Buscar comunicado..."
          emptyMessage="Nenhum comunicado publicado para você até agora."
        />
      </div>

      <ComunicadoSistemaDialog comunicado={aberto} onOpenChange={(o) => !o && setAberto(null)} />
    </div>
  );
}
